import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, ExternalLink } from 'lucide-react';
import { Platform, PLATFORM_NAMES } from '@crosova/shared';
import { api } from '../../lib/api';

const STEPS = [
  { id: 1, title: 'Welcome', desc: 'Set up your profile' },
  { id: 2, title: 'Connect platforms', desc: 'Link your seller accounts' },
  { id: 3, title: 'Choose a plan', desc: 'Start free, upgrade anytime' },
  { id: 4, title: 'Automation', desc: 'Set your preferences' },
  { id: 5, title: 'You\'re ready!', desc: 'Create your first listing' },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040',
  facebook: '#1877f2', grailed: '#888', mercari: '#ff5722',
  poshmark: '#7b1fa2', shopify: '#95bf47', whatnot: '#ffc940',
};

const CORE_PLATFORMS = [Platform.EBAY, Platform.VINTED, Platform.DEPOP, Platform.ETSY, Platform.FACEBOOK];

export function OnboardingFlow() {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [sellsType, setSellsType] = useState('');
  const [autoDelistOn, setAutoDelistOn] = useState(true);
  const navigate = useNavigate();

  async function complete() {
    await Promise.all([
      api.post('/auth/onboarding-complete').catch(() => {}),
      api.patch('/auth/automation-settings', { autoDelistEnabled: autoDelistOn }).catch(() => {}),
    ]);
    navigate('/dashboard/inventory');
  }

  return (
    <div className="min-h-screen flex bg-bg-base">
      {/* Sidebar */}
      <aside className="w-[260px] bg-bg-surface border-r border-white/[0.05] p-6 flex flex-col">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center font-bold text-white text-sm">Cr</div>
          <div className="text-base font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></div>
        </div>

        <div className="flex flex-col gap-1">
          {STEPS.map((s, i) => (
            <div key={s.id}>
              <div className={`flex items-start gap-3 p-2.5 rounded-lg ${step === s.id ? 'bg-primary-500/[0.1]' : ''}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold border-[1.5px] flex-shrink-0 mt-px ${
                  s.id < step
                    ? 'bg-secondary-400 border-secondary-400 text-bg-base'
                    : s.id === step
                    ? 'bg-primary-500 border-primary-500 text-white'
                    : 'border-white/15 text-white/25'
                }`}>
                  {s.id < step ? <Check size={12} strokeWidth={3} /> : s.id}
                </div>
                <div>
                  <div className={`text-[12.5px] font-medium ${s.id === step ? 'text-white' : s.id < step ? 'text-white/40' : 'text-white/25'}`}>{s.title}</div>
                  <div className="text-[10.5px] text-white/25 mt-0.5">{s.desc}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-px h-3 ml-[21px] my-0.5 ${s.id < step ? 'bg-secondary-400/40' : 'bg-white/[0.08]'}`} />
              )}
            </div>
          ))}
        </div>

        <div className="mt-auto">
          <div className="bg-secondary-400/[0.06] border border-secondary-400/[0.15] rounded-xl p-3">
            <div className="text-[11px] font-medium text-secondary-400 mb-0.5">14-day free trial</div>
            <div className="text-[10px] text-white/30">No credit card required to get started.</div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 flex flex-col items-start justify-center p-12 max-w-2xl">
        {step === 1 && (
          <Step1 name={name} setName={setName} sellsType={sellsType} setSellsType={setSellsType} />
        )}
        {step === 2 && <Step2 />}
        {step === 3 && <Step3 />}
        {step === 4 && <Step4 autoDelistOn={autoDelistOn} setAutoDelistOn={setAutoDelistOn} />}
        {step === 5 && <Step5 />}

        <div className="flex gap-3 mt-8">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} className="btn-secondary">Back</button>
          )}
          {step < 5 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !name.trim()}
              className="btn-primary flex items-center gap-1.5"
            >
              Continue <ArrowRight size={13} />
            </button>
          ) : (
            <button onClick={complete} className="btn-primary flex items-center gap-1.5">
              Go to my dashboard <ArrowRight size={13} />
            </button>
          )}
          {step < 5 && (
            <button onClick={() => setStep(step + 1)} className="text-[11px] text-white/25 hover:text-white/50 transition-colors ml-2">
              Skip
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function Step1({ name, setName, sellsType, setSellsType }: {
  name: string; setName: (v: string) => void;
  sellsType: string; setSellsType: (v: string) => void;
}) {
  const SELLS_OPTIONS = [
    { value: 'clothing', label: 'Clothing & footwear' },
    { value: 'electronics', label: 'Electronics & tech' },
    { value: 'homeware', label: 'Homeware & furniture' },
    { value: 'collectibles', label: 'Collectibles & vintage' },
    { value: 'mixed', label: 'Mixed / general' },
  ];

  return (
    <div className="w-full">
      <div className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-3">Step 1 of 5</div>
      <h1 className="text-2xl font-semibold mb-2">Welcome to Crosova</h1>
      <p className="text-sm text-white/40 mb-8">Tell us a bit about yourself so we can tailor the experience.</p>

      <div className="space-y-4">
        <div>
          <label className="text-[11px] font-medium text-white/40 mb-1.5 block">Your name</label>
          <input
            className="input max-w-sm"
            placeholder="e.g. Sarah"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <label className="text-[11px] font-medium text-white/40 mb-1.5 block">What do you mainly sell?</label>
          <div className="flex flex-wrap gap-2">
            {SELLS_OPTIONS.map((o) => (
              <button
                key={o.value}
                onClick={() => setSellsType(o.value)}
                className={`px-3 py-2 rounded-lg border text-[12px] transition-colors ${
                  sellsType === o.value
                    ? 'border-primary-500/40 bg-primary-500/[0.1] text-primary-300'
                    : 'border-white/[0.07] text-white/40 hover:bg-white/[0.03]'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Step2() {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connected, setConnected] = useState<Set<string>>(new Set());

  async function connect(platform: Platform) {
    setConnecting(platform);
    try {
      const { data } = await api.post(`/platforms/${platform}/connect`);
      if (data.authUrl) window.open(data.authUrl, '_blank');
      else setConnected((s) => new Set([...s, platform]));
    } finally {
      setConnecting(null);
    }
  }

  return (
    <div className="w-full">
      <div className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-3">Step 2 of 5</div>
      <h1 className="text-2xl font-semibold mb-2">Connect your accounts</h1>
      <p className="text-sm text-white/40 mb-2">Link the marketplaces you already sell on. You can always add more later in Settings.</p>
      <p className="text-[11px] text-white/25 mb-6">Crosova never stores your passwords — we use official OAuth connections.</p>

      <div className="space-y-2">
        {CORE_PLATFORMS.map((p) => {
          const isConnected = connected.has(p);
          const isConnecting = connecting === p;
          return (
            <div key={p} className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-colors ${isConnected ? 'border-secondary-400/20 bg-secondary-400/[0.04]' : 'border-white/[0.07] bg-white/[0.02]'}`}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: PLATFORM_COLOURS[p] }}>
                  {PLATFORM_NAMES[p].slice(0, 2)}
                </div>
                <div>
                  <div className="text-[13px] font-medium">{PLATFORM_NAMES[p]}</div>
                  <div className="text-[10px] text-white/30">{isConnected ? 'Connected ✓' : 'Not connected'}</div>
                </div>
              </div>
              {isConnected ? (
                <div className="text-[11px] text-secondary-400">Connected</div>
              ) : (
                <button
                  onClick={() => connect(p)}
                  disabled={!!isConnecting}
                  className="btn-secondary !py-1 !px-3 !text-[11px] flex items-center gap-1"
                >
                  {isConnecting ? 'Connecting…' : <><ExternalLink size={11} /> Connect</>}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Step3() {
  const PLANS = [
    {
      id: 'trial',
      name: 'Free trial',
      price: '£0',
      period: '14 days',
      features: ['Up to 50 listings', '3 platforms', 'AI assistant', 'Email support'],
      current: true,
    },
    {
      id: 'starter',
      name: 'Starter',
      price: '£9.99',
      period: '/month',
      features: ['200 listings/mo', '5 platforms', 'Basic AI assistant', 'Email support'],
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '£24.99',
      period: '/month',
      features: ['Unlimited listings', 'All 10 platforms', 'Full AI suite', 'Auto-delist on sale', 'Price advisor', 'Priority support'],
      recommended: true,
    },
  ];

  return (
    <div className="w-full">
      <div className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-3">Step 3 of 5</div>
      <h1 className="text-2xl font-semibold mb-2">Choose your plan</h1>
      <p className="text-sm text-white/40 mb-6">Start free — no credit card needed. Upgrade when you're ready.</p>

      <div className="grid grid-cols-3 gap-3">
        {PLANS.map((plan) => (
          <div key={plan.id} className={`card p-4 relative ${plan.recommended ? 'border-primary-500/30' : ''}`}>
            {plan.recommended && (
              <div className="absolute -top-2.5 left-4 bg-primary-500 text-white text-[9px] font-semibold px-2 py-0.5 rounded">RECOMMENDED</div>
            )}
            {plan.current && (
              <div className="absolute -top-2.5 left-4 bg-white/10 text-white/60 text-[9px] font-semibold px-2 py-0.5 rounded">YOUR PLAN</div>
            )}
            <div className="text-xs text-white/40 mb-1">{plan.name}</div>
            <div className="text-[22px] font-semibold">{plan.price}<span className="text-xs text-white/30 font-normal ml-0.5">{plan.period}</span></div>
            <ul className="mt-3 space-y-1.5 text-[11px]">
              {plan.features.map((f) => (
                <li key={f} className="flex items-center gap-1.5 text-white/55">
                  <Check size={11} className="text-secondary-400 flex-shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {!plan.current && (
              <button
                onClick={() => api.post('/billing/checkout', { plan: plan.id }).then(({ data }) => data.url && (window.location.href = data.url))}
                className={`w-full mt-4 text-[11px] font-medium py-1.5 rounded-lg ${plan.recommended ? 'btn-primary' : 'btn-secondary'}`}
              >
                {plan.recommended ? 'Start with Pro' : 'Choose Starter'}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Step4({ autoDelistOn, setAutoDelistOn }: { autoDelistOn: boolean; setAutoDelistOn: (v: boolean) => void }) {

  return (
    <div className="w-full max-w-lg">
      <div className="text-[11px] font-semibold text-primary-400 uppercase tracking-widest mb-3">Step 4 of 5</div>
      <h1 className="text-2xl font-semibold mb-2">Automation</h1>
      <p className="text-sm text-white/40 mb-6">These save you the most time. You can change them any time in Settings.</p>

      <div className="card divide-y divide-white/[0.04]">
        <Toggle
          label="Auto-delist on sale"
          desc="When an item sells on one platform, automatically remove it from the others — prevents double-selling."
          value={autoDelistOn}
          onChange={setAutoDelistOn}
          recommended
        />
        <div className="px-4 py-3 flex items-start justify-between gap-4 opacity-50">
          <div>
            <div className="flex items-center gap-2 text-[13px] font-medium">
              Price drop reminders
              <span className="text-[9.5px] bg-white/[0.06] text-white/40 px-1.5 py-0.5 rounded">Coming soon</span>
            </div>
            <div className="text-[11px] text-white/30 mt-0.5">Get nudged when similar items sell for less — coming in a future update.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Step5() {
  const navigate = useNavigate();
  return (
    <div className="w-full max-w-lg text-center">
      <div className="w-16 h-16 bg-secondary-400/10 border border-secondary-400/20 rounded-2xl flex items-center justify-center mx-auto mb-5">
        <Check size={28} className="text-secondary-400" strokeWidth={2.5} />
      </div>
      <h1 className="text-2xl font-semibold mb-2">You're all set!</h1>
      <p className="text-sm text-white/40 mb-8">
        Your account is ready. Create your first listing and let Crosova handle the rest.
      </p>
      <div className="grid grid-cols-2 gap-3 text-left">
        <ActionCard
          title="Create a listing"
          desc="Upload photos, fill in details, and crosslist to all your platforms in one go."
          action="New listing"
          onClick={() => navigate('/dashboard/new')}
          primary
        />
        <ActionCard
          title="Explore the dashboard"
          desc="Browse your inventory, check analytics, and manage platform connections."
          action="Go to dashboard"
          onClick={() => navigate('/dashboard/inventory')}
        />
      </div>
    </div>
  );
}

function Toggle({ label, desc, value, onChange, recommended }: {
  label: string; desc: string; value: boolean; onChange: (v: boolean) => void; recommended?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium">{label}</span>
          {recommended && <span className="text-[9px] font-semibold px-1.5 py-0.5 bg-secondary-400/10 text-secondary-400 rounded">RECOMMENDED</span>}
        </div>
        <p className="text-[11px] text-white/30 mt-0.5">{desc}</p>
      </div>
      <button
        onClick={() => onChange(!value)}
        className={`w-9 h-5 rounded-full relative transition-colors flex-shrink-0 mt-0.5 ${value ? 'bg-primary-500' : 'bg-white/10'}`}
      >
        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-[3px] transition-all ${value ? 'left-[19px]' : 'left-[3px]'}`} />
      </button>
    </div>
  );
}

function ActionCard({ title, desc, action, onClick, primary }: {
  title: string; desc: string; action: string; onClick: () => void; primary?: boolean;
}) {
  return (
    <div className={`card p-4 flex flex-col gap-3 ${primary ? 'border-primary-500/20' : ''}`}>
      <div>
        <div className="text-[13px] font-semibold mb-1">{title}</div>
        <p className="text-[11px] text-white/40">{desc}</p>
      </div>
      <button onClick={onClick} className={primary ? 'btn-primary text-[11px]' : 'btn-secondary text-[11px]'}>
        {action}
      </button>
    </div>
  );
}
